import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import type { Employee } from '@lms/shared-types'
import { Button } from '../primitives/Button'
import { listEmployeesPage } from '../../api/employees'
import { displayName } from '../../lib/display-name'
import { useDebounce } from '../../lib/use-debounce'

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  minHeight: 44,
  boxSizing: 'border-box',
}

const selectedRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 12px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  minHeight: 44,
}

const listboxStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(80,56,28,0.18)',
  zIndex: 20,
  maxHeight: 280,
  overflowY: 'auto',
}

const optionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  width: '100%',
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  textAlign: 'left',
  cursor: 'pointer',
  minHeight: 44,
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface EmployeePickerInputProps {
  /** Currently selected employee, or null when empty. */
  value: Employee | null
  onChange: (employee: Employee | null) => void
  /** testid prefix; child elements are `${testId}-input`, `-listbox`, `-option`, `-clear`. */
  testId: string
  placeholder?: string
  /** Employee id to exclude from results (prevents self-delegation). */
  excludeId?: string | undefined
  /** Label shown on the "deselect" button when an employee is selected.
   *  Defaults to "× Clear". */
  clearLabel?: string
  /**
   * Custom search function. Defaults to `listEmployeesPage` (HR-only endpoint).
   * Callers in non-HR contexts (e.g. manager delegation) pass a function that
   * hits an endpoint accessible to their role — see `listDelegateCandidates`.
   *
   * The function should return a list of Employee-shaped objects; the picker
   * only renders id / name / preferredName / email / jobTitle, so synthetic
   * Employee objects with empty values for unused fields are accepted.
   */
  searchFn?: (params: { search?: string; limit: number; page: number }) => Promise<{ data: Employee[] }>
  /**
   * Render the results dropdown in a fixed-position portal on `document.body`
   * instead of absolutely inside the wrapper. Use inside a modal / any
   * `overflow` container that would otherwise clip the dropdown. Off by default
   * so inline page usages are unchanged.
   */
  portalMenu?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Employee autocomplete picker.
 *
 * Debounces the search query (200 ms), hits `listEmployeesPage({ search })`,
 * and renders a floating listbox of matching employees. Selecting an employee
 * stores the full `Employee` object. Shows a clear affordance when a value is
 * selected.
 *
 * testId conventions (for `data-testid`):
 *   - `${testId}`          — wrapper (always present)
 *   - `${testId}-input`    — search input (visible when no value selected)
 *   - `${testId}-listbox`  — dropdown (visible when open)
 *   - `${testId}-option`   — each employee option button
 *   - `${testId}-clear`    — clear / change button (visible when value selected)
 */
export function EmployeePickerInput({
  value,
  onChange,
  testId,
  placeholder,
  excludeId,
  clearLabel = '× Clear',
  searchFn,
  portalMenu = false,
}: Readonly<EmployeePickerInputProps>): JSX.Element {
  const [search, setSearch] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const debounced = useDebounce(search, 200)

  // Portal-menu anchoring: track the input's viewport rect so the fixed-
  // position dropdown sits flush under it (and follows scroll / resize).
  const inputRef = useRef<HTMLInputElement>(null)
  const [menuRect, setMenuRect] = useState<{
    left: number
    top: number
    width: number
  } | null>(null)
  useLayoutEffect(() => {
    if (!portalMenu || !open) {
      setMenuRect(null)
      return
    }
    const update = (): void => {
      const el = inputRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setMenuRect({ left: r.left, top: r.bottom + 4, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [portalMenu, open])

  const defaultSearch = (params: { search?: string; limit: number; page: number }) =>
    listEmployeesPage({
      active: true,
      limit: params.limit,
      page: params.page,
      ...(params.search ? { search: params.search } : {}),
    })
  const effectiveSearch = searchFn ?? defaultSearch

  const employeesQuery = useQuery({
    queryKey: ['employees-search', searchFn ? 'custom' : 'default', debounced],
    queryFn: () =>
      effectiveSearch({
        limit: 20,
        page: 1,
        ...(debounced ? { search: debounced } : {}),
      }),
    staleTime: 30_000,
    enabled: open,
    // Keep the previous results visible while a refetch is in flight. Without
    // this, the listbox flashes "Searching…" between every keystroke once the
    // 200ms debounce fires — and the option buttons unmount/remount in that
    // window, which destabilises Playwright's actionability checks (the demo
    // recording's smoothClick → scrollIntoViewIfNeeded races the remount).
    placeholderData: (prev) => prev,
  })

  const results = (employeesQuery.data?.data ?? []).filter((e) => e.id !== excludeId)

  // ── Selected state ───────────────────────────────────────────────────────────

  if (value) {
    return (
      <div data-testid={testId} style={selectedRowStyle}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
            {displayName(value)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {value.jobTitle} · {value.email}
          </span>
        </div>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => {
            onChange(null)
            setSearch('')
          }}
          data-testid={`${testId}-clear`}
        >
          {clearLabel}
        </Button>
      </div>
    )
  }

  // ── Search state ─────────────────────────────────────────────────────────────

  const menuOpen = open && Boolean(search || employeesQuery.data)
  const menu = menuOpen ? (
    <div
      role="listbox"
      style={
        portalMenu && menuRect
          ? {
              ...listboxStyle,
              position: 'fixed',
              top: menuRect.top,
              left: menuRect.left,
              right: 'auto',
              width: menuRect.width,
              zIndex: 3000,
            }
          : listboxStyle
      }
      data-testid={`${testId}-listbox`}
    >
      {employeesQuery.isLoading ? (
        <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>Searching…</div>
      ) : results.length === 0 ? (
        <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>No matches.</div>
      ) : (
        results.map((emp) => (
          <button
            key={emp.id}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              onChange(emp)
              setSearch('')
              setOpen(false)
            }}
            data-testid={`${testId}-option`}
            style={optionStyle}
          >
            <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
              {displayName(emp)}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
              {emp.jobTitle} · {emp.email}
            </span>
          </button>
        ))
      )}
    </div>
  ) : null

  return (
    <div style={{ position: 'relative' }} data-testid={testId}>
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder ?? 'Search…'}
        style={inputStyle}
        data-testid={`${testId}-input`}
      />
      {portalMenu ? (menu ? createPortal(menu, document.body) : null) : menu}
    </div>
  )
}
